import React, { FC, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { productionDashboardDataTemplate } from 'apollo/templates/order';
import { departmentsTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import ProductionDashboardTable from 'components/production-dashboard/overview/production-dashboard-table.component';
import ProductionDashboardDetail from 'components/production-dashboard/department-detail/production-dashboard-detail.component';
import LateOrdersBanner from 'components/production-dashboard/overview/late-orders-banner.component';
import PausedOrdersSection from 'components/production-dashboard/overview/paused-orders-section.component';
import WeekNavigation from 'components/production-dashboard/overview/week-navigation.component';
import { Department } from 'models/organization';
import { ScheduledStep, ScheduleStatus } from 'models/production-planning';
import { getToday } from 'components/production-dashboard/utils';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

type ProductionDashboardDataResponse = {
    productionDashboardData: {
        orderId: string;
        orderDisplayId?: string;
        stepId: string;
        stepName: string;
        currentStepName?: string;
        workStarted?: boolean;
        department?: string;
        technicianName?: string;
        startDate?: string;
        deadline: string;
        scheduleStatus: ScheduleStatus;
        estimatedProductionHours?: number;
        clinicName?: string;
        deliveryClinicName?: string;
        dentistName?: string;
        patientName?: string;
        isPaused?: boolean;
        pauseReason?: string;
        lastActionByName?: string;
        lastActionType?: string;
    }[];
};

const ProductionDashboard: FC = () => {
    const { t: translate } = useTranslation(['common', 'production-dashboard']);
    const [searchParams, setSearchParams] = useSearchParams();
    const [weekStart, setWeekStart] = useState<Date>(() => getToday());
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();

    const selectedDepartment = searchParams.get('department');
    const selectedDateStr = searchParams.get('date');
    const selectedDate = selectedDateStr ? new Date(selectedDateStr + 'T00:00:00') : null;

    const toDate = new Date(weekStart);
    toDate.setDate(toDate.getDate() + 14);

    const { data, loading, refetch } = useQuery<ProductionDashboardDataResponse>(productionDashboardDataTemplate, {
        fetchPolicy: 'network-only',
        pollInterval: 2 * 60 * 1000,
        variables: { fromDate: weekStart, toDate },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: departmentsData } = useQuery(departmentsTemplate, {
        fetchPolicy: 'network-only',
        // DD-3589: the menu hides this page from external users, but the URL still opens it.
        skip: !permissions.canReadDepartments(currentUser.role),
    });
    const departments: Department[] = departmentsData?.departments || [];

    const scheduledSteps: ScheduledStep[] = (data?.productionDashboardData || []).map((step) => ({
        orderId: step.orderId,
        orderDisplayId: step.orderDisplayId,
        stepId: step.stepId,
        stepName: step.stepName,
        currentStepName: step.currentStepName || undefined,
        workStarted: step.workStarted ?? false,
        isPaused: step.isPaused ?? false,
        pauseReason: step.pauseReason || undefined,
        lastActionByName: step.lastActionByName || undefined,
        lastActionType: step.lastActionType || undefined,
        department: step.department || '',
        technicianName: step.technicianName || '',
        startDate: step.startDate ? new Date(step.startDate) : null,
        deadline: new Date(step.deadline),
        scheduleStatus: step.scheduleStatus || ScheduleStatus.ON_SCHEDULE,
        estimatedProductionHours: step.estimatedProductionHours,
        clinicName: step.clinicName || undefined,
        deliveryClinicName: step.deliveryClinicName || undefined,
        dentistName: step.dentistName || undefined,
        patientName: step.patientName || undefined,
    }));

    const handlePrev = () => {
        setWeekStart((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() - 14);
            return d;
        });
    };

    const handleNext = () => {
        setWeekStart((prev) => {
            const d = new Date(prev);
            d.setDate(d.getDate() + 14);
            return d;
        });
    };

    const handleToday = () => {
        setWeekStart(getToday());
    };

    const isAtToday = weekStart.getTime() === getToday().getTime();

    const handleCellClick = (department: string, date: Date) => {
        const y = date.getFullYear();
        const m = (date.getMonth() + 1).toString().padStart(2, '0');
        const d = date.getDate().toString().padStart(2, '0');
        setSearchParams({ department, date: `${y}-${m}-${d}` });
    };

    const handleBack = () => {
        setSearchParams({});
    };

    const selectedStep = searchParams.get('step');

    // DD-3786: by step id, so the schedule still finds orders planned before a step was renamed.
    const handleShowSchedule = (stepId: string) => {
        setSearchParams({ department: selectedDepartment!, date: selectedDateStr!, step: stepId });
    };

    const handleBackFromSchedule = () => {
        setSearchParams({ department: selectedDepartment!, date: selectedDateStr! });
    };

    const activeDepartment = selectedDepartment ? departments.find((d) => d.name === selectedDepartment) : null;

    const lateOrders = useMemo(() => {
        const seen = new Map<string, string>();
        for (const step of scheduledSteps) {
            if (step.scheduleStatus === ScheduleStatus.LATE && !seen.has(step.orderDisplayId || '')) {
                seen.set(step.orderDisplayId || '', step.orderId);
            }
        }
        return Array.from(seen.entries()).map(([orderDisplayId, mongoId]) => ({ orderDisplayId, mongoId }));
    }, [scheduledSteps]);

    // One row per paused order: its PAUSED step (DD-3558), or any of its rows when its
    // current step is not in the plan and no step is PAUSED (DD-3605).
    const pausedSteps = useMemo(() => {
        const stepByOrder = new Map<string, ScheduledStep>();
        for (const step of scheduledSteps) {
            if (!step.isPaused) continue;
            if (!stepByOrder.has(step.orderId) || step.scheduleStatus === ScheduleStatus.PAUSED) {
                stepByOrder.set(step.orderId, step);
            }
        }
        return Array.from(stepByOrder.values());
    }, [scheduledSteps]);

    return (
        <>
            <Helmet>
                <title>{translate('production-dashboard:PRODUCTION_DASHBOARD')}</title>
            </Helmet>

            {activeDepartment && selectedDate ? (
                <ProductionDashboardDetail
                    department={activeDepartment}
                    date={selectedDate}
                    scheduledSteps={scheduledSteps}
                    onBack={handleBack}
                    selectedStep={selectedStep}
                    onShowSchedule={handleShowSchedule}
                    onBackFromSchedule={handleBackFromSchedule}
                    onStepChanged={() => refetch()}
                />
            ) : (
                <>
                    <LateOrdersBanner lateOrders={lateOrders} />
                    <PausedOrdersSection pausedSteps={pausedSteps} />
                    <WeekNavigation
                        onPrev={handlePrev}
                        onNext={handleNext}
                        onToday={handleToday}
                        isPrevDisabled={isAtToday}
                    />
                    {loading ? (
                        <Skeleton variant="rectangular" height={300} />
                    ) : (
                        <ProductionDashboardTable
                            currentWeekStart={weekStart}
                            scheduledSteps={scheduledSteps}
                            departments={departments}
                            onCellClick={handleCellClick}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default ProductionDashboard;
